/**
 * User preferences (FR-SET-01).
 *
 * Stored on `users/{uid}.preferences` so they follow the user across devices rather than
 * living in localStorage. The period basis in particular is a reporting decision, not a
 * device setting — FY on the desktop and calendar on the phone would be a bug.
 */

import { doc, setDoc } from 'firebase/firestore';
import type { PeriodBasis } from '../core/period';
import type { DateDisplayFormat } from '../core/dates';
import { db } from './firebase';
import { trackWrite } from './syncState';

export interface Preferences {
  periodBasis: PeriodBasis;
  defaultAccountId: string | null;
  defaultCurrency: 'INR' | 'USD';
  budgetWarnPercent: number;
  dateFormat: DateDisplayFormat;
}

/**
 * Defaults chosen for this user's context, not generic ones: India, so FY Apr–Mar and
 * dd-mm-yyyy rather than the workbook's mm-dd-yy (defect 6).
 */
export const DEFAULT_PREFERENCES: Preferences = {
  periodBasis: 'fy',
  defaultAccountId: null,
  defaultCurrency: 'INR',
  budgetWarnPercent: 80,
  dateFormat: 'dd-mm-yyyy',
};

export function withDefaults(stored: Partial<Preferences> | undefined): Preferences {
  return { ...DEFAULT_PREFERENCES, ...(stored ?? {}) };
}

export async function savePreferences(
  uid: string,
  preferences: Partial<Preferences>,
): Promise<void> {
  await trackWrite(
    setDoc(doc(db, 'users', uid), { preferences }, { merge: true }),
  );
}
